import React, { useState, useEffect, useRef } from "react";
import { FaVideo, FaUpload } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchCurriculumByClassAdmin } from "../../../services/operations/curriculumAPI";
import toast from "react-hot-toast";
import { uploadVideo } from "../../../services/operations/videoUploadAPI";

export default function VideoUpload() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // Curriculum data states
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [topic, setTopic] = useState("");
  const [curriculumData, setCurriculumData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [videoFile, setVideoFile] = useState(null);

  const classes = ["6", "7", "8", "9", "10"];

  // Fetch curriculum when class changes
  useEffect(() => {
    const loadCurriculum = async () => {
      if (!selectedClass) {
        setCurriculumData(null);
        setSubjects([]);
        setChapters([]);
        setSelectedSubject("");
        setSelectedChapter("");
        return;
      }

      const data = await dispatch(fetchCurriculumByClassAdmin(selectedClass));
      if (data && data.subjects) {
        setCurriculumData(data);
        const subjectList = Object.keys(data.subjects).map((key) => ({
          id: key,
          name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
        }));
        setSubjects(subjectList);
      } else {
        setCurriculumData(null);
        setSubjects([]);
      }
      setSelectedSubject("");
      setSelectedChapter("");
    };

    loadCurriculum();
  }, [selectedClass, dispatch]);

  // Update chapters when subject changes
  useEffect(() => {
    if (selectedSubject && curriculumData?.subjects?.[selectedSubject]?.chapters) {
      setChapters(curriculumData.subjects[selectedSubject].chapters);
    } else {
      setChapters([]);
    }
    setSelectedChapter("");
  }, [selectedSubject, curriculumData]);

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) setVideoFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoFile || !selectedSubject || !selectedChapter || !topic || !selectedClass) {
      toast.error(t("videoUpload.fillAllFields"));
      return;
    }

    const formData = new FormData();
    formData.append("subject", selectedSubject);
    formData.append("chapter", selectedChapter);
    formData.append("topic", topic);
    formData.append("className", selectedClass);
    formData.append("video", videoFile);

    try {
      await uploadVideo(formData);
      toast.success(t("videoUpload.successMessage"));
    } catch (err) {
      toast.error(err.message || t("videoUpload.uploadFailed"));
    } finally {
      setVideoFile(null);
      setSelectedClass("");
      setSelectedSubject("");
      setSelectedChapter("");
      setTopic("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <main className="text-[#1B1F3B] min-h-screen p-6">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <header className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-4">
            <div className="bg-[#DBEAFE] rounded-2xl p-3">
              <FaVideo className="w-8 h-8 text-[#2563EB]" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold leading-tight">
                {t("videoUpload.title")}
              </h1>
              <p className="text-sm text-[#4B5563]">
                {t("videoUpload.subtitle")}
              </p>
            </div>
          </div>
        </header>

        {/* Upload Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6"
        >
          {/* Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("videoUpload.class")}
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring focus:ring-indigo-200"
            >
              <option value="">{t("videoUpload.selectClass")}</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>
                  {t("videoUpload.classLabel", { num: cls })}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("videoUpload.subject")}
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={!selectedClass}
              className={`w-full border border-gray-300 rounded-lg p-2 focus:ring focus:ring-indigo-200 ${
                !selectedClass ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            >
              <option value="">
                {selectedClass
                  ? t("videoUpload.selectSubject")
                  : t("videoUpload.selectClassFirst")}
              </option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("videoUpload.chapter")}
            </label>
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject}
              className={`w-full border border-gray-300 rounded-lg p-2 focus:ring focus:ring-indigo-200 ${
                !selectedSubject ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            >
              <option value="">
                {selectedSubject
                  ? t("videoUpload.selectChapter")
                  : t("videoUpload.selectSubjectFirst")}
              </option>
              {chapters.map((chapter, idx) => (
                <option key={idx} value={chapter}>
                  {chapter}
                </option>
              ))}
            </select>
          </div>

          {/* Topic */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("videoUpload.topic")}
            </label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("videoUpload.topicPlaceholder")}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring focus:ring-indigo-200"
            />
          </div>

          {/* Upload Video */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("videoUpload.uploadVideo")}
            </label>
            <div className="flex items-center justify-between border border-dashed border-gray-300 rounded-lg p-4 hover:border-indigo-400 transition">
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
                ref={fileInputRef}
                className="text-sm text-gray-600 w-full"
              />
              <FaUpload className="text-indigo-500 w-6 h-6 ml-2" />
            </div>
            {videoFile && (
              <p className="mt-2 text-sm text-green-600">
                ✅ {videoFile.name} {t("videoUpload.selected")}
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="text-right">
            <button
              type="submit"
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
            >
              {t("videoUpload.submit")}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
